/**
 * ContractSigningPage — capture customer + dealer signatures + GPS pin,
 * upload the signature images, and POST a fully-formed contract to
 * /api/contracts.
 *
 * Closes the last go-live blocker called out in the customer/dealer
 * audit. Today the server already accepts everything we send here
 * (signedByCustomer / signedByDealer / dealerGpsAtSignature) — there
 * was just no UI capturing the inputs.
 *
 * Flow:
 *   1. Fetch loan + customer + device + dealer for the page.
 *   2. Show a contract summary the customer + dealer can review.
 *   3. SignaturePad ×2 + GpsPinPicker.
 *   4. On submit:
 *      a. Upload both signature PNGs via POST /api/documents (kind=signature).
 *      b. Upload a JSON summary as pdfRef (a real PDF renderer is a
 *         follow-up; the server's contract canonical-JSON is what's
 *         signed cryptographically, so a placeholder ref is OK for v0).
 *      c. POST /api/contracts with both refs + snapshots + GPS.
 *      d. On success, route back to /loans/<id>.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import {
  ApiError,
  type Contract,
  type Customer,
  type Dealer,
  type Device,
  type Loan,
} from "@dfp/api-client";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { GpsPinPicker, type GpsPin } from "../components/GpsPinPicker.js";
import { SignaturePad, type SignaturePadHandle } from "../components/SignaturePad.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

export function ContractSigningPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const auth = useAuth();
  const navigate = useNavigate();
  const customerSigRef = useRef<SignaturePadHandle | null>(null);
  const dealerSigRef = useRef<SignaturePadHandle | null>(null);
  const [gps, setGps] = useState<GpsPin | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loanQuery = useQuery({
    queryKey: ["loans", "byId", id],
    queryFn: () => api.get<Loan>(`/api/loans/${id}`),
  });
  const loan = loanQuery.data;
  // loans.contract_id is NOT NULL (auto-generated UUID if no contract
  // was supplied at loan creation), so "already signed?" means the
  // contract row actually resolves. 404 = unsigned.
  const contractQuery = useQuery({
    queryKey: ["contracts", "byId", loan?.contractId ?? "none"],
    queryFn: () => api.get<Contract>(`/api/contracts/${loan!.contractId}`),
    enabled: Boolean(loan?.contractId),
    retry: false,
  });
  const customerQuery = useQuery({
    queryKey: ["customers", "byId", loan?.customerId ?? "none"],
    queryFn: () => api.get<Customer>(`/api/customers/${loan!.customerId}`),
    enabled: Boolean(loan?.customerId),
  });
  const deviceQuery = useQuery({
    queryKey: ["devices", "byId", loan?.deviceId ?? "none"],
    queryFn: () => api.get<Device>(`/api/devices/${loan!.deviceId}`),
    enabled: Boolean(loan?.deviceId),
  });
  const dealerQuery = useQuery({
    queryKey: ["dealers", "byId", loan?.dealerId ?? "none"],
    queryFn: () => api.get<Dealer>(`/api/dealers/${loan!.dealerId}`),
    enabled: Boolean(loan?.dealerId),
  });

  const submit = useMutation({
    mutationFn: signContract,
    onSuccess: () => {
      void navigate({ to: "/loans/$id", params: { id } });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Sign rejected");
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Unexpected error");
      }
    },
  });

  if (loanQuery.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (loanQuery.isError || !loan) {
    return <p className="text-sm text-red-700">Failed to load loan.</p>;
  }
  // If the contract row actually exists, the loan is already signed.
  if (contractQuery.isSuccess && contractQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Already signed</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2 text-sm">
          <p className="text-slate-700">
            This loan already has a contract on file (
            <span className="font-mono">{contractQuery.data.id.slice(0, 8)}</span>
            ). Re-signing is not supported.
          </p>
          <Button onClick={() => void navigate({ to: "/loans/$id", params: { id } })}>
            Back to loan
          </Button>
        </CardBody>
      </Card>
    );
  }

  const customer = customerQuery.data;
  const device = deviceQuery.data;
  const dealer = dealerQuery.data;
  const scheduled = loan.schedule.reduce(
    (s, r) => s + r.principalMinor + r.interestMinor + r.feesMinor,
    0,
  );

  async function onSubmit(): Promise<void> {
    setError(null);
    if (!auth.user) {
      setError("Not signed in.");
      return;
    }
    if (customerSigRef.current?.isEmpty() ?? true) {
      setError("Customer signature is required.");
      return;
    }
    if (dealerSigRef.current?.isEmpty() ?? true) {
      setError("Dealer signature is required.");
      return;
    }
    if (gps === null) {
      setError("GPS pin is required (use 'Use my location' or click on the map).");
      return;
    }
    submit.mutate({
      loanId: loan!.id,
      auth,
      customerSigBase64: customerSigRef.current!.toBase64()!,
      dealerSigBase64: dealerSigRef.current!.toBase64()!,
      gps,
      loan: loan!,
      customer: customer!,
      device: device!,
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Sign contract</h1>
        <p className="text-sm text-slate-500">
          Loan <span className="font-mono">{id}</span>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Review</CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <Row k="Customer" v={customer ? `${customer.givenName} ${customer.familyName}` : "…"} />
          <Row k="Dealer" v={dealer?.name ?? "…"} />
          <Row k="Device" v={device ? `${device.brand} ${device.model}` : "…"} />
          <Row k="IMEI" v={device?.imei ?? "…"} />
          <Row k="Principal" v={formatMoney(loan.principalAmountMinor, loan.principalCurrency)} />
          <Row k="Deposit" v={formatMoney(loan.depositAmountMinor, loan.depositCurrency)} />
          <Row k="Total scheduled" v={formatMoney(scheduled, loan.principalCurrency)} />
          <Row k="Installments" v={`${loan.schedule.length}`} />
          <Row k="Frequency" v={loan.repaymentFrequency ?? "—"} />
          <Row k="Tenor" v={loan.tenorMonths ? `${loan.tenorMonths} months` : "—"} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Signatures</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <SignaturePad ref={customerSigRef} label="Customer signature" />
          <SignaturePad ref={dealerSigRef} label="Dealer signature" />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>GPS at signing</CardTitle>
        </CardHeader>
        <CardBody>
          <GpsPinPicker label="Signing location" value={gps} onChange={setGps} />
        </CardBody>
      </Card>

      {error ? (
        <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          onClick={() => void navigate({ to: "/loans/$id", params: { id } })}
        >
          Cancel
        </Button>
        <Button onClick={() => void onSubmit()} disabled={submit.isPending}>
          {submit.isPending ? "Signing…" : "Sign + submit"}
        </Button>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-slate-500">{k}</span>
      <span className="font-medium text-slate-900">{v}</span>
    </div>
  );
}

/* ─── Submit pipeline ──────────────────────────────────────────────── */

interface SignArgs {
  loanId: string;
  auth: ReturnType<typeof useAuth>;
  customerSigBase64: string;
  dealerSigBase64: string;
  gps: GpsPin;
  loan: Loan;
  customer: Customer;
  device: Device;
}

async function signContract(args: SignArgs): Promise<Contract> {
  // 1) Upload both signatures as kind=signature documents. Refs flow
  //    into signedByCustomer / signedByDealer (the server accepts free-
  //    form strings; we agree platform-side that the value is an
  //    object-store ref).
  const customerRef = await uploadSignature(args.customerSigBase64);
  const dealerRef = await uploadSignature(args.dealerSigBase64);

  // 2) Snapshots. v0 captures the loan + device facts as the
  //    canonical frozen JSON. A future PDF generator can render this
  //    plus the rules document into a human-readable PDF.
  const loanTermsSnapshot = {
    loanId: args.loan.id,
    customerId: args.loan.customerId,
    dealerId: args.loan.dealerId,
    principalAmountMinor: args.loan.principalAmountMinor,
    principalCurrency: args.loan.principalCurrency,
    depositAmountMinor: args.loan.depositAmountMinor,
    depositCurrency: args.loan.depositCurrency,
    repaymentFrequency: args.loan.repaymentFrequency,
    tenorMonths: args.loan.tenorMonths,
  };
  const deviceTermsSnapshot = {
    deviceId: args.device.id,
    imei: args.device.imei,
    serial: args.device.serial,
    brand: args.device.brand,
    model: args.device.model,
    valueAmountMinor: args.device.valueAmountMinor,
    valueCurrency: args.device.valueCurrency,
  };
  const lockPolicySnapshot = {
    policyVersionId: args.loan.policyVersionId,
  };
  const paymentScheduleSnapshot = {
    schedule: args.loan.schedule,
  };

  // 3) PDF placeholder — the canonical JSON the server hashes IS the
  //    legal record. We upload the JSON summary as a document so a
  //    persisted ref exists; once a real PDF renderer ships, the
  //    refresh-on-display path renders the same canonical bytes.
  const pdfRef = await uploadJson({
    loanTermsSnapshot,
    deviceTermsSnapshot,
    lockPolicySnapshot,
    paymentScheduleSnapshot,
    customerSignatureRef: customerRef,
    dealerSignatureRef: dealerRef,
    gps: args.gps,
    signedAt: new Date().toISOString(),
  });

  // 4) Sign.
  return api.post<Contract>("/api/contracts", {
    loanId: args.loan.id,
    templateId: "mvp-v1",
    version: 1,
    language: args.customer.language || "en",
    signedByCustomer: customerRef,
    signedByDealer: dealerRef,
    loanTermsSnapshot,
    deviceTermsSnapshot,
    lockPolicySnapshot,
    paymentScheduleSnapshot,
    pdfRef,
    dealerGpsAtSignature: {
      lat: args.gps.lat,
      lng: args.gps.lng,
      accuracyMetres: args.gps.accuracyM ?? 0,
    },
    dealerDeviceFingerprint:
      typeof window !== "undefined"
        ? `${window.navigator.userAgent}-${args.auth.user?.sub ?? "anon"}`
        : "server",
  });
}

async function uploadSignature(base64: string): Promise<string> {
  const res = await api.post<{ ref: string }>("/api/documents", {
    kind: "signature",
    contentType: "image/png",
    dataBase64: base64,
  });
  return res.ref;
}

async function uploadJson(obj: Record<string, unknown>): Promise<string> {
  const json = JSON.stringify(obj, null, 2);
  // Encode UTF-8 → base64 via a TextEncoder + btoa fallback.
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]!);
  }
  const base64 = btoa(binary);
  const res = await api.post<{ ref: string }>("/api/documents", {
    kind: "other",
    contentType: "application/pdf",
    dataBase64: base64,
  });
  return res.ref;
}
